// OfferPerformanceTab — Phase 15.7 admin Performance tab.
//
// Glass-morphism stat cards + sleek CSS flex/grid progress bars.
// No third-party charting library — everything is Tailwind utility
// classes so the bundle stays lean and the dark luxury aesthetic is
// uniform with the rest of the admin UI.

import { useEffect, useState } from 'react';
import { levelName } from '../lib/levelNames';
import Icon from '@baaligames/shared-ui/components/Icon';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { ApiError } from '@baaligames/shared-ui/lib/api';

type HealthStatus = 'healthy' | 'warning' | 'critical' | 'idle' | 'exhausted' | 'expired' | 'paused' | 'archived';

type Stats = {
  summary: {
    claims_total: number;
    claims_credited: number;
    claims_pending: number;
    claims_rejected: number;
    amount_credited_total: number;
    amount_platform_funded: number;
    amount_agent_funded: number;
    platform_savings_pct: number;
    global_budget: number | null;
    budget_used: number;
    budget_pct: number | null;
    opted_in_agent_count: number;
    // Phase 16.3 additions
    eligible_player_count: number | null;
    claims_per_day_7d: number;
    claims_per_day_30d: number;
    days_to_budget_exhaustion: number | null;
    remaining_budget: number | null;
    rejection_pct: number;
    avg_platform_cost_per_claim: number;
    avg_agent_subsidy_per_claim: number;
    conversion_pct: number | null;
    health_status: HealthStatus;
    health_message: string;
  };
  per_agent: Array<{
    agent_id: number; username: string; level: number | null;
    claims_count: number; total_paid: number;
    share_pct_avg: number; overdraft_count: number;
  }>;
  daily_series: Array<{
    date: string; claims_credited: number; amount: number;
  }>;
};

const inr = (n: number) => `₹${(n || 0).toLocaleString('en-IN')}`;

export default function OfferPerformanceTab({ offerId }: { offerId: number }) {
  const [stats, setStats] = useState<Stats | null>(null);
  const [err, setErr]     = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await adminApi.get<Stats>(`/admin/offers/${offerId}/stats`);
        if (!cancelled) setStats(r);
      } catch (e) {
        if (!cancelled) setErr((e as ApiError).message || 'Could not load stats');
      }
    })();
    return () => { cancelled = true; };
  }, [offerId]);

  if (err) {
    return (
      <div className="rounded-xl bg-rose-50 dark:bg-rose-900/30 ring-1 ring-inset ring-rose-200/60 dark:ring-rose-800/50 text-rose-700 dark:text-rose-200 px-4 py-3 text-sm">
        {err}
      </div>
    );
  }
  if (!stats) {
    return (
      <div className="rounded-xl bg-arena-50/40 dark:bg-arena-800/40 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 p-4 text-sm text-arena-600 dark:text-neon-200/60">
        Loading performance data…
      </div>
    );
  }

  const s = stats.summary;
  const platformPct = s.amount_credited_total > 0
    ? (s.amount_platform_funded / s.amount_credited_total) * 100 : 100;
  const agentPct = 100 - platformPct;
  const maxDailyAmount = Math.max(1, ...stats.daily_series.map((d) => d.amount));

  return (
    <div className="space-y-4">
      {/* Phase 16.3 — status banner. ALWAYS the first thing the
          admin sees. Plain English sentence + color-coded chip. */}
      <StatusBanner status={s.health_status} message={s.health_message} />

      {/* Phase 16.3 — conversion funnel. Bottleneck visible at a glance. */}
      <Funnel
        optedInAgents={s.opted_in_agent_count}
        eligiblePlayers={s.eligible_player_count}
        claimsFired={s.claims_total}
        credited={s.claims_credited}
        conversionPct={s.conversion_pct}
      />

      {/* Phase 16.3 — per-claim economics. Unit economics. */}
      <PerClaimEconomics
        avgPlatformPerClaim={s.avg_platform_cost_per_claim}
        avgAgentPerClaim={s.avg_agent_subsidy_per_claim}
        avgClaimAmount={s.claims_credited > 0 ? s.amount_credited_total / s.claims_credited : 0}
      />

      <p className="text-xs text-arena-400 dark:text-neon-200/70">
        Drill-down below: aggregate cost split, per-agent contribution, daily
        credit volume.
      </p>

      {/* ─── Top stat cards ────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          label="Claims credited"
          value={s.claims_credited.toLocaleString('en-IN')}
          sub={`${s.claims_pending} pending · ${s.claims_rejected} rejected`}
          tone="cyan"
          icon="check_circle"
        />
        <StatCard
          label="Total credited"
          value={inr(s.amount_credited_total)}
          sub={s.global_budget != null ? `${s.budget_pct?.toFixed(1)}% of ₹${s.global_budget.toLocaleString('en-IN')} budget` : 'No budget cap'}
          tone="brand"
          icon="payments"
        />
        <StatCard
          label="Platform cost"
          value={inr(s.amount_platform_funded)}
          sub={`${platformPct.toFixed(1)}% of total`}
          tone="amber"
          icon="account_balance"
        />
        <StatCard
          label="Agent-subsidized"
          value={inr(s.amount_agent_funded)}
          sub={`Saved platform ${s.platform_savings_pct.toFixed(1)}%`}
          tone="emerald"
          icon="trending_down"
          highlight
        />
      </div>

      {/* ─── Cost split bar ─────────────────────────────────── */}
      <div className="rounded-2xl bg-white/70 dark:bg-arena-700/40
                      ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 p-4 space-y-3">
        <div className="flex items-baseline justify-between gap-2">
          <h4 className="text-[11px] uppercase tracking-wider font-bold text-arena-600 dark:text-neon-200/55">
            Cost split
          </h4>
          <span className="text-[10px] text-arena-400 dark:text-neon-200/70">
            of {inr(s.amount_credited_total)} credited
          </span>
        </div>
        {s.amount_credited_total === 0 ? (
          <div className="text-xs text-arena-600 dark:text-neon-200/60">No credited claims yet.</div>
        ) : (
          <>
            <div className="flex h-8 rounded-lg overflow-hidden
                            bg-arena-100/60 dark:bg-arena-800
                            ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40">
              <div className="bg-gradient-to-r from-amber-400 to-amber-500
                              flex items-center justify-end pr-2"
                   style={{ width: `${platformPct}%` }}>
                {platformPct >= 12 && (
                  <span className="text-[10.5px] font-bold text-white drop-shadow tabular-nums">
                    {platformPct.toFixed(1)}%
                  </span>
                )}
              </div>
              <div className="bg-gradient-to-r from-emerald-500 to-emerald-400
                              flex items-center justify-start pl-2"
                   style={{ width: `${agentPct}%` }}>
                {agentPct >= 12 && (
                  <span className="text-[10.5px] font-bold text-white drop-shadow tabular-nums">
                    {agentPct.toFixed(1)}%
                  </span>
                )}
              </div>
            </div>
            <div className="flex justify-between text-[10.5px] uppercase tracking-wider font-bold">
              <span className="text-amber-700 dark:text-amber-300">
                Platform · {inr(s.amount_platform_funded)}
              </span>
              <span className="text-emerald-700 dark:text-emerald-400">
                Agents · {inr(s.amount_agent_funded)}
              </span>
            </div>
          </>
        )}
      </div>

      {/* ─── Per-agent contribution ────────────────────────── */}
      <div className="rounded-2xl bg-white/70 dark:bg-arena-700/40
                      ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 overflow-hidden">
        <div className="px-4 py-2.5 border-b ring-arena-200/50 dark:ring-arena-600/40
                        bg-arena-50/60 dark:bg-arena-800/40
                        flex items-baseline justify-between">
          <h4 className="text-[11px] uppercase tracking-wider font-bold text-arena-600 dark:text-neon-200/55">
            Per-agent contribution
          </h4>
          <span className="text-[10px] text-arena-400 dark:text-neon-200/70">{stats.per_agent.length} agent{stats.per_agent.length === 1 ? '' : 's'}</span>
        </div>
        {stats.per_agent.length === 0 ? (
          <div className="px-4 py-4 text-xs text-arena-600 dark:text-neon-200/60">
            No agent has contributed yet. Either no opt-ins or no credited claims.
          </div>
        ) : (
          <div className="divide-y divide-arena-200/40 dark:divide-arena-700/40/70">
            {stats.per_agent.map((a) => {
              const pctOfAgentTotal = s.amount_agent_funded > 0
                ? (a.total_paid / s.amount_agent_funded) * 100 : 0;
              return (
                <div key={a.agent_id} className="px-4 py-3 grid grid-cols-1 sm:grid-cols-[1fr_auto_120px] gap-2 items-center">
                  <div className="min-w-0">
                    <div className="text-[13px] font-bold text-neutral-900 dark:text-white truncate">
                      @{a.username}
                    </div>
                    <div className="text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/60">
                      {levelName(a.level)} · {a.claims_count} claim{a.claims_count === 1 ? '' : 's'} · avg {a.share_pct_avg}%
                      {a.overdraft_count > 0 && (
                        <span className="ml-1 text-rose-600 dark:text-rose-400">· {a.overdraft_count} overdraft</span>
                      )}
                    </div>
                  </div>
                  <div className="font-display font-extrabold text-base text-emerald-700 dark:text-emerald-400 tabular-nums sm:text-right">
                    {inr(a.total_paid)}
                  </div>
                  <div className="hidden sm:block w-full h-1.5 rounded-full
                                  bg-arena-100/60 dark:bg-arena-800 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-emerald-500 to-cyan-400"
                         style={{ width: `${pctOfAgentTotal}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ─── Daily series ──────────────────────────────────── */}
      <div className="rounded-2xl bg-white/70 dark:bg-arena-700/40
                      ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 p-4 space-y-3">
        <div className="flex items-baseline justify-between gap-2">
          <h4 className="text-[11px] uppercase tracking-wider font-bold text-arena-600 dark:text-neon-200/55">
            Last 30 days
          </h4>
          <span className="text-[10px] text-arena-400 dark:text-neon-200/70">
            credited amount per day
          </span>
        </div>
        {stats.daily_series.length === 0 ? (
          <div className="text-xs text-arena-600 dark:text-neon-200/60">No credited claims in the last 30 days.</div>
        ) : (
          <div className="flex items-end gap-1 h-24">
            {stats.daily_series.map((d) => {
              const heightPct = (d.amount / maxDailyAmount) * 100;
              return (
                <div key={d.date} className="flex-1 flex flex-col items-center justify-end group">
                  <div className="w-full bg-gradient-to-t from-cyan-500 to-cyan-300
                                  rounded-t hover:from-brand-500 hover:to-brand-300 transition"
                       style={{ height: `${Math.max(2, heightPct)}%` }}
                       title={`${d.date}: ${inr(d.amount)} (${d.claims_credited} claims)`} />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Stat card ───────────────────────────────────────────────

// ─── Phase 16.3 components ─────────────────────────────────

function StatusBanner({ status, message }: { status: HealthStatus; message: string }) {
  // Per locked decision D3: full plain-English sentence, NOT a terse pill.
  // Color + icon convey severity at a glance; the sentence carries the
  // actionable detail.
  const map: Record<HealthStatus, { ring: string; bg: string; text: string; icon: string; label: string }> = {
    healthy:   { ring: 'ring-emerald-300/60 dark:ring-emerald-400/40', bg: 'bg-emerald-50 dark:bg-emerald-500/10', text: 'text-emerald-800 dark:text-emerald-200', icon: 'check_circle',   label: 'Healthy' },
    warning:   { ring: 'ring-amber-300/70   dark:ring-amber-400/40',   bg: 'bg-amber-50   dark:bg-amber-500/10',   text: 'text-amber-800   dark:text-amber-200',   icon: 'warning',        label: 'Warning' },
    critical:  { ring: 'ring-rose-300/70    dark:ring-rose-400/40',    bg: 'bg-rose-50    dark:bg-rose-500/10',    text: 'text-rose-800    dark:text-rose-200',    icon: 'cancel',         label: 'Critical' },
    exhausted: { ring: 'ring-rose-300/70    dark:ring-rose-400/40',    bg: 'bg-rose-50    dark:bg-rose-500/10',    text: 'text-rose-800    dark:text-rose-200',    icon: 'block',          label: 'Exhausted' },
    expired:   { ring: 'ring-arena-300/60   dark:ring-arena-600/40',       bg: 'bg-arena-100/60 dark:bg-arena-700/40',  text: 'text-arena-700 dark:text-neon-200/75', icon: 'event_busy',     label: 'Expired' },
    paused:    { ring: 'ring-amber-300/70   dark:ring-amber-400/40',   bg: 'bg-amber-50   dark:bg-amber-500/10',   text: 'text-amber-800   dark:text-amber-200',   icon: 'pause_circle',   label: 'Paused' },
    archived:  { ring: 'ring-arena-300/60   dark:ring-arena-600/40',       bg: 'bg-arena-100/60 dark:bg-arena-700/40',  text: 'text-arena-700 dark:text-neon-200/75', icon: 'inventory_2',    label: 'Archived' },
    idle:      { ring: 'ring-arena-300/60   dark:ring-arena-600/40',       bg: 'bg-arena-100/60 dark:bg-arena-700/40',  text: 'text-arena-700 dark:text-neon-200/75', icon: 'hourglass_empty',label: 'Idle' },
  };
  const t = map[status] || map.idle;
  return (
    <div className={`rounded-2xl ${t.bg} ${t.text} ring-1 ${t.ring}
                     px-4 py-3 flex items-start gap-3`}>
      <Icon name={t.icon} size={22} className="shrink-0 mt-0.5" filled />
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-[.22em] font-extrabold opacity-80">
          {t.label}
        </div>
        <div className="text-[13.5px] leading-snug font-semibold mt-0.5">
          {message}
        </div>
      </div>
    </div>
  );
}

function Funnel({
  optedInAgents, eligiblePlayers, claimsFired, credited, conversionPct,
}: {
  optedInAgents: number;
  eligiblePlayers: number | null;
  claimsFired: number;
  credited: number;
  conversionPct: number | null;
}) {
  const fmt = (n: number | null) => n == null ? '—' : n.toLocaleString('en-IN');
  // Stage list — each is one "card" in the horizontal flow.
  const stages = [
    { label: 'Opted-in agents',   value: fmt(optedInAgents),    sub: 'cost-share active' },
    { label: 'Eligible players',  value: fmt(eligiblePlayers),  sub: 'pass audience targets' },
    { label: 'Claims fired',      value: fmt(claimsFired),       sub: 'all states' },
    { label: 'Credited',          value: fmt(credited),          sub: 'paid out' },
    { label: 'Conversion',        value: conversionPct == null ? '—' : `${conversionPct.toFixed(1)}%`, sub: 'credited / eligible', highlight: true },
  ];
  return (
    <div className="rounded-2xl bg-white/70 dark:bg-arena-700/40
                    ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 p-4">
      <div className="flex items-baseline justify-between gap-2 mb-3">
        <h4 className="text-[11px] uppercase tracking-wider font-bold text-arena-600 dark:text-neon-200/55">
          Conversion funnel
        </h4>
        <span className="text-[10px] text-arena-400 dark:text-neon-200/70">click any drop to investigate the bottleneck</span>
      </div>
      <div className="flex flex-col sm:flex-row sm:items-stretch gap-2">
        {stages.map((s, i) => (
          <div key={s.label} className="flex sm:flex-col items-center sm:items-stretch flex-1 gap-2">
            <div className={`flex-1 rounded-xl px-3 py-2.5 border
                             ${s.highlight
                               ? 'bg-cyan-50 dark:bg-cyan-400/10 border-cyan-300 dark:border-cyan-400/30 text-cyan-900 dark:text-cyan-200'
                               : 'bg-arena-50/40 dark:bg-arena-800/40 ring-arena-200/50 dark:ring-arena-600/40'}`}>
              <div className="text-[9.5px] uppercase tracking-[.18em] font-bold text-arena-600 dark:text-neon-200/55 truncate">
                {s.label}
              </div>
              <div className={`font-display font-black text-2xl tabular-nums leading-none mt-1
                               ${s.highlight ? '' : 'text-neutral-900 dark:text-white'}`}>
                {s.value}
              </div>
              <div className="text-[10px] text-arena-600 dark:text-neon-200/55 mt-1 truncate">
                {s.sub}
              </div>
            </div>
            {i < stages.length - 1 && (
              <div className="grid place-items-center text-arena-500 dark:text-neon-200/45
                              sm:px-0 px-2 self-center">
                <Icon name="arrow_forward" size={18} className="hidden sm:block" />
                <Icon name="arrow_downward" size={18} className="sm:hidden" />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function PerClaimEconomics({
  avgPlatformPerClaim, avgAgentPerClaim, avgClaimAmount,
}: {
  avgPlatformPerClaim: number;
  avgAgentPerClaim: number;
  avgClaimAmount: number;
}) {
  const total = avgPlatformPerClaim + avgAgentPerClaim;
  const platformPct = total > 0 ? (avgPlatformPerClaim / total) * 100 : 100;
  const agentPct    = total > 0 ? (avgAgentPerClaim    / total) * 100 : 0;
  return (
    <div className="rounded-2xl bg-white/70 dark:bg-arena-700/40
                    ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 p-4 space-y-3">
      <div className="flex items-baseline justify-between gap-2">
        <h4 className="text-[11px] uppercase tracking-wider font-bold text-arena-600 dark:text-neon-200/55">
          Per-claim economics
        </h4>
        <span className="text-[10px] text-arena-400 dark:text-neon-200/70 tabular-nums">
          avg credit ₹{avgClaimAmount.toFixed(2)}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-arena-600 dark:text-neon-200/55">
            Platform pays per claim
          </div>
          <div className="font-display font-black text-2xl tabular-nums text-amber-700 dark:text-amber-300">
            ₹{avgPlatformPerClaim.toFixed(2)}
          </div>
          <div className="text-[10px] text-arena-600 dark:text-neon-200/60">{platformPct.toFixed(1)}% of avg credit</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-arena-600 dark:text-neon-200/55">
            Agents subsidize per claim
          </div>
          <div className="font-display font-black text-2xl tabular-nums text-emerald-700 dark:text-emerald-300">
            ₹{avgAgentPerClaim.toFixed(2)}
          </div>
          <div className="text-[10px] text-arena-600 dark:text-neon-200/60">{agentPct.toFixed(1)}% of avg credit</div>
        </div>
      </div>
    </div>
  );
}

function StatCard({
  label, value, sub, tone, icon, highlight,
}: {
  label: string;
  value: string;
  sub?: string;
  tone: 'cyan' | 'brand' | 'amber' | 'emerald';
  icon: string;
  highlight?: boolean;
}) {
  const tones = {
    cyan:    { ring: 'ring-cyan-200/60 dark:ring-cyan-400/30',     accent: 'text-cyan-700 dark:text-cyan-300',    glow: 'shadow-[0_0_24px_rgba(143,255,254,0.18)]' },
    brand:   { ring: 'ring-brand-200/60 dark:ring-brand-400/30',   accent: 'text-brand-700 dark:text-brand-300',  glow: 'shadow-[0_0_24px_rgba(240,126,51,0.18)]' },
    amber:   { ring: 'ring-amber-200/60 dark:ring-amber-400/30',   accent: 'text-amber-700 dark:text-amber-300',  glow: 'shadow-[0_0_24px_rgba(251,191,36,0.18)]' },
    emerald: { ring: 'ring-emerald-200/60 dark:ring-emerald-400/30', accent: 'text-emerald-700 dark:text-emerald-300', glow: 'shadow-[0_0_24px_rgba(52,211,153,0.20)]' },
  } as const;
  const t = tones[tone];
  return (
    <div className={`relative rounded-2xl p-3.5 backdrop-blur
                     bg-white/70 dark:bg-arena-700/40
                     ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                     ring-1 ${t.ring}
                     ${highlight ? t.glow : ''}`}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon name={icon} size={14} className={t.accent} filled />
        <span className="text-[9.5px] uppercase tracking-[.18em] font-bold text-arena-600 dark:text-neon-200/55">
          {label}
        </span>
      </div>
      <div className={`font-display font-black text-xl tabular-nums ${t.accent}`}>
        {value}
      </div>
      {sub && (
        <div className="text-[10px] text-arena-600 dark:text-neon-200/55 mt-0.5 truncate">
          {sub}
        </div>
      )}
    </div>
  );
}
